import { VIDEO_MAX_BYTES, type VideoDetailType } from "@volgo/core/contract";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Link, type Location, useBlocker, useLocation } from "react-router";
import { useSession } from "../session";
import { Dialog } from "../ui/Dialog";
import { useMedia } from "../ui/Sidebar";
import { useLoad } from "../ui/useLoad";
import { bytes, useVisiblePoll, VideoStateNote, videoChanged } from "./shared";
import { UploadCommon } from "./UploadCommon";
import { editable, needsFile, needsLeaveWarning, UploadQueue, type UploadRow } from "./uploadQueue";
import { VideoFields } from "./VideoFields";

const ACCEPT = ".mp4,.m4v,.mov,.mkv,.webm,.avi,.wmv,.flv,.ts,.m2ts,.3gp";
const transferBytes = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1024 ** 2
      ? `${(n / 1024).toFixed(1)} KiB`
      : n < 1024 ** 3
        ? `${(n / 1024 ** 2).toFixed(1)} MiB`
        : bytes(n);
const uploadLocation = (location: Location) => {
  const background = (location.state as { background?: Location } | null)?.background;
  return location.pathname.startsWith("/g/") && background?.pathname === "/videos/upload"
    ? background
    : location;
};
const labels: Record<UploadRow["phase"], string> = {
  draft: "준비",
  queued: "전송 대기",
  creating: "등록 중",
  uploading: "전송 중",
  paused: "일시정지",
  missing: "파일 재선택",
  ambiguous: "등록 결과 불명",
  transferred: "전송 완료 · 처리 접수 확인 중",
  processing: "서버 처리",
  published: "공개",
  failed: "처리 실패",
  "cancel-failed": "취소 확인 필요",
  cancelled: "취소됨",
  error: "확인 필요",
};

export function UploadPage() {
  const { state } = useSession();
  return <UploadWorkspace key={state.user?.id ?? 0} userId={state.user?.id ?? 0} />;
}

function UploadWorkspace({ userId }: { userId: number }) {
  const { client } = useSession();
  const location = useLocation();
  const queue = useMemo(() => new UploadQueue(client, userId, videoChanged), [client, userId]);
  const snapshot = useSyncExternalStore(queue.subscribe, queue.getSnapshot);
  const { rows, running, stopReason, storageWarning } = snapshot;
  const series = useLoad(
    () => client.call("videos.seriesList", { query: { mine: true, limit: 100 } }),
    rows.map((r) => r.form.seriesId ?? "").join(","),
  );
  const narrow = useMedia("(max-width: 1023px)");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [commonOpen, setCommonOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [confirmation, setConfirmation] = useState<{ id: string; restart: boolean } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const cancelRef = useRef(false);
  const detailPanel = useRef<HTMLElement>(null);
  const focusFile = () =>
    requestAnimationFrame(() => {
      (
        detailPanel.current?.querySelector<HTMLInputElement>('input[type="file"]') ??
        detailPanel.current?.querySelector<HTMLInputElement>("input")
      )?.focus();
    });
  const retryHandled = useRef<string | null>(null);
  const retry = (location.state as { retry?: VideoDetailType } | null)?.retry;
  useEffect(() => {
    queue.activate();
    void queue.poll();
    const offline = () => queue.pause("오프라인입니다. 연결이 돌아오면 이어 올리기를 눌러 주세요.");
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("offline", offline);
      queue.close();
    };
  }, [queue]);
  useEffect(() => {
    if (!retry || retryHandled.current === retry.id) return;
    retryHandled.current = retry.id;
    setActiveId(
      queue.addRetry(
        {
          title: retry.title,
          description: retry.description,
          tags: retry.tags,
          gids: retry.links.map((g) => g.gid),
          seriesId: retry.series?.id ?? null,
          episodeNo: retry.series?.episodeNo ?? null,
        },
        retry.sourceName ?? "다시 올릴 영상",
        retry.sourceBytes ?? 0,
        retry.id,
      ),
    );
    setNotice("처리에 실패한 영상의 정보를 가져왔습니다. 파일을 선택하면 새로 전송합니다.");
  }, [queue, retry]);
  const leaveWarning = rows.some(needsLeaveWarning);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const current = uploadLocation(currentLocation),
      next = uploadLocation(nextLocation);
    return leaveWarning && (current.pathname !== next.pathname || current.search !== next.search);
  });
  useEffect(() => {
    if (!leaveWarning) return;
    const leaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", leaving);
    return () => window.removeEventListener("beforeunload", leaving);
  }, [leaveWarning]);
  useVisiblePoll(
    rows.some((r) => r.ticket && !["published", "failed", "cancelled"].includes(r.phase)),
    () => queue.poll(),
  );
  const active = rows.find((r) => r.id === activeId) ?? rows[0];
  const selectable = rows.filter(editable);
  const selectedCount = selectable.filter((r) => selected.includes(r.id)).length;
  const targets = rows.filter((r) => r.target);
  const total = targets.reduce((n, r) => n + r.size, 0);
  const done = targets.reduce((n, r) => n + r.done, 0);
  const transferred = targets.filter((r) => r.done === r.size && r.size > 0).length;
  const allTransferred = targets.length > 0 && transferred === targets.length;
  const allPublished = targets.length > 0 && targets.every((r) => r.phase === "published");
  const canStart = rows.some((r) => ["draft", "queued", "paused", "error", "missing"].includes(r.phase));
  const hasBatch = targets.length > 0;
  const appendable = selectable.filter((r) => !r.target);
  const openDetail = (id: string) => {
    setActiveId(id);
    setDetailOpen(true);
  };
  const add = (files: File[]) => {
    const ids = queue.add(files);
    if (!active && ids[0]) setActiveId(ids[0]);
    setNotice(
      `${files.length}개 파일을 추가했습니다.${hasBatch ? " 이번 전송에 추가를 눌러 전송 대상에 넣으세요." : ""}`,
    );
  };
  const recoverMany = (files: File[]) => {
    const unmatched = queue.attachMany(files);
    setNotice(
      unmatched.length
        ? `${unmatched.length}개 파일은 일치하는 항목이 없거나 중복 후보가 있습니다. 각 영상 상세에서 다시 선택하세요.`
        : `${files.length}개 파일을 연결했습니다. 이어 올리기를 눌러 주세요.`,
    );
  };
  const controls = (row: UploadRow) => (
    <>
      {needsFile(row) && (
        <label className="upload-reselect">
          {row.modified === 0 && !row.ticket ? "새로 올릴 파일 선택" : "같은 파일 다시 선택"}
          <input
            aria-label={`${row.name} 다시 선택`}
            type="file"
            accept={ACCEPT}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) queue.attach(row.id, file, !row.ticket && row.modified === 0);
              e.target.value = "";
            }}
          />
        </label>
      )}
      {row.ticket && row.phase !== "published" && row.phase !== "cancelled" && (
        <button
          type="button"
          className="btn"
          onClick={() => {
            void queue.check(row.id).catch(() => {});
          }}
        >
          상태 확인
        </button>
      )}
      {row.file && ["paused", "error"].includes(row.phase) && (
        <button
          type="button"
          className="btn"
          disabled={running}
          onClick={() => {
            void queue.start([row.id]);
          }}
        >
          이 파일 이어 올리기
        </button>
      )}
      {row.phase === "failed" && (
        <button
          type="button"
          className="btn"
          onClick={() => {
            queue.retryFailed(row.id);
            focusFile();
          }}
        >
          파일 다시 선택해 새로 올리기
        </button>
      )}
      {row.ticket &&
        Date.parse(row.ticket.expiresAt) <= Date.now() &&
        !["processing", "published", "failed", "cancelled", "transferred"].includes(row.phase) && (
          <button
            type="button"
            className="btn"
            onClick={() => setConfirmation({ id: row.id, restart: true })}
          >
            기존 업로드 취소 후 새로 올리기
          </button>
        )}
      {(row.ticket || row.phase === "creating") &&
        !["published", "cancelled", "failed"].includes(row.phase) && (
          <button
            type="button"
            className="btn"
            onClick={() => setConfirmation({ id: row.id, restart: false })}
          >
            {row.phase === "cancel-failed" ? "취소 다시 시도" : "업로드 취소"}
          </button>
        )}
      {row.phase === "published" && (
        <>
          <Link className="btn btn-pri" to={`/videos/${row.ticket?.videoId}`}>
            영상 보기
          </Link>
          {row.codec && row.codec !== "h264" && <p>일부 기기에서 재생되지 않을 수 있습니다 ({row.codec})</p>}
        </>
      )}
      {(row.phase === "ambiguous" || row.ticket) && (
        <Link className="btn" to="/videos/mine">
          올린 영상에서 확인
        </Link>
      )}
    </>
  );
  const detail = active ? (
    <section ref={detailPanel} className="upload-detail video-form" aria-label="선택 영상 상세">
      <div className="upload-section-head">
        <h2>영상 상세</h2>
        {narrow && (
          <button type="button" className="btn" onClick={() => setDetailOpen(false)}>
            목록으로
          </button>
        )}
      </div>
      <p className="upload-file-name">{active.name}</p>
      <small>
        {transferBytes(active.size)} · {labels[active.phase]}
      </small>
      {active.error && <p role="alert">{active.error}</p>}
      {active.phase === "ambiguous" && (
        <p>
          서버에 이미 등록됐을 수 있어 자동으로 다시 등록하지 않습니다. 올린 영상에서 등록 결과를 확인하세요.
        </p>
      )}
      {active.ticket && (
        <p>
          등록된 영상 정보는 <Link to="/videos/mine">올린 영상</Link>에서 수정할 수 있습니다.
        </p>
      )}
      {editable(active) && (
        <div className="upload-row-actions">
          <button
            type="button"
            className="btn"
            aria-label={`${active.name} 위로`}
            disabled={selectable[0]?.id === active.id}
            onClick={() => queue.move(active.id, -1)}
          >
            위로
          </button>
          <button
            type="button"
            className="btn"
            aria-label={`${active.name} 아래로`}
            disabled={selectable.at(-1)?.id === active.id}
            onClick={() => queue.move(active.id, 1)}
          >
            아래로
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              queue.remove(active.id);
              setDetailOpen(false);
            }}
          >
            목록에서 제거
          </button>
        </div>
      )}
      <fieldset disabled={!editable(active)}>
        <VideoFields key={active.id} value={active.form} onChange={(form) => queue.edit(active.id, form)} />
      </fieldset>
      {active.state && active.state.status !== "uploading" && (
        <VideoStateNote state={active.state} name={active.name} />
      )}
      <div className="video-actions">{controls(active)}</div>
      {active.ticket &&
        !["processing", "published", "failed", "cancelled", "transferred"].includes(active.phase) && (
          <p>
            이어 올리기는 등록 후 24시간까지 가능합니다. 같은 이름·크기·수정 시각의 파일이 필요합니다.
            만료되면 기존 업로드를 취소한 뒤 새로 올려 주세요.
          </p>
        )}
    </section>
  ) : (
    <section className="upload-detail upload-detail-empty">
      <h2>영상 정보를 한 곳에서</h2>
      <p>
        파일을 추가하고 목록에서 상세 편집을 여세요. 서로 다른 영상도, 같은 작품의 여러 회차도 한 번에 준비할
        수 있습니다.
      </p>
    </section>
  );
  return (
    <div className="page video-upload">
      <div className="page-head">
        <div>
          <h1>영상 올리기</h1>
          <p>파일마다 확인하고, 순서대로 올리세요.</p>
        </div>
        <Link className="btn" to="/videos/mine">
          올린 영상
        </Link>
      </div>
      <p className="upload-help">
        파일당 최대 {VIDEO_MAX_BYTES / 1024 ** 3}GiB · 처리 후 바로 공개 · H.264 영상이 가장 많은 기기에서
        재생됩니다.
      </p>
      {storageWarning && (
        <p role="alert" className="upload-banner">
          {storageWarning}
        </p>
      )}
      {stopReason && (
        <p role="alert" className="upload-banner">
          {stopReason}
        </p>
      )}
      <p className="upload-announcement" aria-live="polite" aria-atomic="true">
        {notice}
      </p>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop is an alternative to the labelled file input */}
      <div
        className="video-drop"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          add(Array.from(e.dataTransfer.files));
        }}
      >
        <label className="upload-file-picker btn">
          영상 파일 고르기
          <input
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(e) => {
              add(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
        </label>
        <p>
          {rows.length
            ? "파일을 더 선택하거나 여기에 놓으세요. 기존 목록은 유지됩니다."
            : "여러 영상 파일을 여기에 놓거나 선택하세요. 다음 화면 없이 여기서 모두 준비할 수 있습니다."}
        </p>
      </div>
      {rows.some(needsFile) && (
        <div className="upload-recovery upload-banner">
          <h2>중단한 업로드 이어 올리기</h2>
          <p>
            파일 본문은 브라우저에 저장하지 않습니다. 같은 파일을 다시 선택하면 서버가 받은 위치부터
            이어집니다.
          </p>
          <label>
            여러 파일 다시 선택
            <input
              type="file"
              multiple
              accept={ACCEPT}
              onChange={(e) => {
                recoverMany(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
          </label>
        </div>
      )}
      <div className="upload-workspace">
        <section className="upload-list" aria-label="업로드 대기열">
          <div className="upload-section-head">
            <h2>
              대기열 <span>{rows.length}</span>
            </h2>
            <button
              type="button"
              className="btn"
              disabled={selectable.length < 2}
              onClick={() => queue.sort()}
            >
              파일 이름순 정렬
            </button>
          </div>
          {rows.length > 0 && (
            <div className="upload-selection">
              <label>
                <input
                  type="checkbox"
                  checked={selectable.length > 0 && selectedCount === selectable.length}
                  disabled={!selectable.length}
                  onChange={(e) => setSelected(e.target.checked ? selectable.map((r) => r.id) : [])}
                />
                전송 전 전체 선택
              </label>
              <button
                type="button"
                className="btn"
                disabled={!selectedCount}
                onClick={() => setCommonOpen(!commonOpen)}
              >
                선택 {selectedCount}개 공통 설정
              </button>
            </div>
          )}
          {commonOpen && (
            <UploadCommon
              queue={queue}
              rows={rows}
              selected={selected}
              onDone={() => {
                setCommonOpen(false);
                setNotice("공통 설정을 닫았습니다. 적용된 정보는 각 상세에서 확인할 수 있습니다.");
              }}
            />
          )}
          <ol className="upload-rows">
            {rows.map((row) => (
              <li
                key={row.id}
                data-upload={row.id}
                data-phase={row.phase}
                data-active={active?.id === row.id}
              >
                <div className="upload-row-heading">
                  <label className="upload-checkbox">
                    <input
                      type="checkbox"
                      aria-label={`${row.name} 선택`}
                      checked={selected.includes(row.id)}
                      disabled={!editable(row)}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? [...selected, row.id] : selected.filter((id) => id !== row.id),
                        )
                      }
                    />
                  </label>
                  <button
                    className="upload-row-title"
                    type="button"
                    onClick={() => openDetail(row.id)}
                    aria-label={`${row.name} 상세 편집`}
                  >
                    <strong>{row.form.title || "제목 없음"}</strong>
                    <span>{row.name}</span>
                  </button>
                  <span className="upload-row-state" role="status">
                    {row.phase === "queued"
                      ? `전송 대기 ${rows.filter((r) => r.target && r.phase === "queued").findIndex((r) => r.id === row.id) + 1}번째`
                      : labels[row.phase]}
                  </span>
                </div>
                <p className="upload-row-meta">
                  {transferBytes(row.size)} ·{" "}
                  {row.form.seriesId
                    ? `${series.data?.items.find((s) => s.id === row.form.seriesId)?.title ?? "지정한 작품"} · ${row.form.episodeText ?? row.form.episodeNo ?? 1}회차`
                    : "독립 영상"}
                </p>
                {row.duplicate && (
                  <p>이름·크기·수정 시각이 같은 항목이 있습니다. 같은 내용인지는 확인하지 않았습니다.</p>
                )}
                {row.ticket && (
                  <>
                    <progress
                      value={row.done}
                      max={Math.max(1, row.size)}
                      aria-label={`${row.name} 파일 전송 진행률`}
                    />
                    <p className="upload-transfer-text">
                      {transferBytes(row.done)} / {transferBytes(row.size)} ·{" "}
                      {row.size ? Math.round((row.done / row.size) * 100) : 0}%
                      {row.rate > 0 &&
                        row.phase === "uploading" &&
                        ` · ${(row.rate / 1024 ** 2).toFixed(1)} MiB/s · 약 ${Math.ceil((row.size - row.done) / row.rate)}초 남음`}
                    </p>
                  </>
                )}
                {row.state && row.state.status !== "uploading" && (
                  <VideoStateNote state={row.state} name={row.name} />
                )}
                {row.error && <p className="upload-row-error">{row.error}</p>}
                {row.phase === "published" && (
                  <Link className="btn" to={`/videos/${row.ticket?.videoId}`}>
                    영상 보기
                  </Link>
                )}
              </li>
            ))}
          </ol>
          {!rows.length && (
            <p className="upload-empty">
              아직 추가한 파일이 없습니다. 파일을 고르면 개별 정보와 전송 상태가 이곳에 표시됩니다.
            </p>
          )}
        </section>
        {!narrow && detail}
      </div>
      {narrow && detailOpen && active && (
        <Dialog
          label="영상 상세 편집"
          drawer
          className="drawer upload-detail-dialog"
          onClose={() => setDetailOpen(false)}
        >
          {detail}
        </Dialog>
      )}
      <section className="upload-bottom" aria-label="전체 전송 제어">
        <div>
          <strong>
            {allPublished
              ? "모든 영상 공개 완료"
              : allTransferred
                ? "모든 파일 전송 완료"
                : hasBatch
                  ? `${transferred} / ${targets.length}개 전송 완료`
                  : `${rows.length}개 파일 준비`}
          </strong>
          {hasBatch && (
            <>
              <progress value={done} max={Math.max(1, total)} aria-label="전체 파일 전송 진행률" />
              <p>
                {transferBytes(done)} / {transferBytes(total)} 전송
              </p>
            </>
          )}
          <p>
            {allTransferred
              ? "화면을 닫아도 서버 처리는 계속됩니다."
              : "탭을 닫으면 전송이 멈춥니다. 서버 처리는 계속됩니다."}
          </p>
        </div>
        <div className="upload-bottom-actions">
          {rows.some((r) => r.error || r.phase === "ambiguous") && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                const first = rows.find((r) => r.error || r.phase === "ambiguous");
                if (first) openDetail(first.id);
              }}
            >
              첫 오류 확인
            </button>
          )}
          {hasBatch && appendable.length > 0 && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                queue.include(appendable.map((r) => r.id));
                setNotice("이번 전송 대상에 추가했습니다. 일시정지 중이면 이어 올리기를 눌러 주세요.");
              }}
            >
              이번 전송에 추가 ({appendable.length})
            </button>
          )}
          {running && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                queue.pause();
                setNotice("현재 파일 전송을 멈추고 있습니다. 서버 처리는 계속됩니다.");
              }}
            >
              일시 정지
            </button>
          )}
          <button
            type="button"
            className="btn btn-pri"
            disabled={!canStart || running}
            onClick={() => {
              void queue.start(hasBatch ? targets.map((r) => r.id) : undefined);
              setNotice("입력을 확인한 뒤 준비된 영상을 순서대로 전송합니다.");
            }}
          >
            {hasBatch ? "이어 올리기" : "준비된 영상 올리기"}
          </button>
        </div>
      </section>
      {confirmation && (
        <Dialog
          label="업로드 취소 확인"
          className="modal-small"
          onClose={() => {
            if (!cancelling) setConfirmation(null);
          }}
        >
          <h2>
            {confirmation.restart ? "기존 업로드를 취소하고 새로 올릴까요?" : "이 업로드를 취소할까요?"}
          </h2>
          <p>{rows.find((r) => r.id === confirmation.id)?.name}</p>
          <p>
            {confirmation.restart
              ? "기존 업로드 삭제가 확인된 뒤 같은 정보로 새 업로드를 준비합니다. 다시 전송하면 0%부터 시작합니다."
              : "받은 파일과 진행 중인 서버 처리가 삭제됩니다. 다른 파일의 정보는 유지됩니다."}
          </p>
          <div className="video-actions">
            <button
              type="button"
              className="btn btn-pri"
              disabled={cancelling}
              onClick={async () => {
                if (cancelRef.current) return;
                cancelRef.current = true;
                setCancelling(true);
                await queue.cancel(confirmation.id, confirmation.restart);
                setConfirmation(null);
                setCancelling(false);
                cancelRef.current = false;
              }}
            >
              {cancelling ? "취소 확인 중" : "업로드 취소 확인"}
            </button>
            <button type="button" className="btn" disabled={cancelling} onClick={() => setConfirmation(null)}>
              계속 올리기
            </button>
          </div>
        </Dialog>
      )}
      {blocker.state === "blocked" && (
        <Dialog label="업로드 화면에서 나가기" className="modal-small" onClose={() => blocker.reset()}>
          <h2>업로드 화면에서 이동할까요?</h2>
          <p>
            파일 전송이 중단됩니다. 정보는 복구할 수 있지만 이어 올리려면 같은 파일을 다시 선택해야 합니다.
            서버 처리는 계속됩니다.
          </p>
          <div className="video-actions">
            <button type="button" className="btn btn-pri" onClick={() => blocker.reset()}>
              계속 올리기
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                queue.pause();
                blocker.proceed();
              }}
            >
              이동하기
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
